// sections/SectionAction.jsx
import { useState } from 'react';
import { CheckCircle, ArrowRightCircle } from 'lucide-react';
import { Row, Col, Button, SectionTitle } from '../../../SharedInputs.jsx';
import {
  updateAgentApplication,
  createNewAgentApplication
} from '../../../../services/AgentService.js';
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { CODE_TO_COUNTRY } from '../../../../CountrySACC.js';
import ImageModule from "docxtemplater-image-module-free";

export function SectionAction({ state: initialData, onCancel, setParentInitialData }) {
  const [error, setError] = useState('');
  const getCountryCode = (countryCode) => CODE_TO_COUNTRY[countryCode] || '';
  const accepted = initialData?.status === "active";
  const pending = initialData?.status === "new";
  
  // Agent agreement template path
  const agentAgreementTemplatePath = "/templates/Education Agent application form V5.0 April 2026.docx";

  const mapAgent = (data) => ({
    ...data,
    agentName: data.companyDetails.companyName || "",
    agentCode: "-",
    email: data.companyDetails.email || "",
    phone: data.companyDetails.phone || "",
    address: data.companyDetails.address || "",
    commissionRate: 0,
    notes: "",
    status: "active",
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const handleAcceptAgent = async () => {
    if (!initialData?.id || accepted) return;

    try {
      const mappedAgentData = mapAgent(initialData);
      let response = (initialData.id === "") ? initialData?.id : "";
      
      if (response !== "") {
        await updateAgentApplication(initialData.id, mappedAgentData);
      } else {
        response = await createNewAgentApplication(mappedAgentData, initialData.id);
      }
      
      setParentInitialData({ 
        ...initialData, 
        status: "active", 
        agentAcceptedAt: new Date().toISOString()
      });
    } catch (err) {
      console.log("Error", err);
      setError(err.message);
    }
  };

  const processDocxTemplate = (arrayBuffer, mapping) => {
    const zip = new PizZip(arrayBuffer);
    
    // Update the getSize function in processDocxTemplate to handle signature sizing:
    const imageOpts = {
      centered: false,
      getImage: (tagValue) => {
        if (tagValue && tagValue.startsWith('data:image')) {
          const base64Data = tagValue.split(',')[1];
          const binaryString = atob(base64Data);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return bytes;
        }
        const binaryString = atob(tagValue);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      },
      getSize: (img, tagValue, tagName) => {
        // ADD THIS: Signature-specific sizing
        if (tagName === 'REPRESENTATIVE_SIGNATURE') {
          return [200, 80]; // Signature size: width 200px, height 80px
        }
        return [250, 100]; // Default size
      }
    };

    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: "[[", end: "]]" },
      modules: [new ImageModule(imageOpts)]
    });

    try {
      doc.render(mapping);
    } catch (error) {
      throw error;
    }

    const out = doc.getZip().generate({ type: "blob" });
    return out;
  };

  const createAgentAgreementMapping = (data) => {
    console.log("Data: ", data);
    return {
      // ============ COMPANY DETAILS ============
      'COMPANY_NAME': data.companyDetails?.companyName || data.companyDetails?.tradingName || '',
      'TRADING_NAME': data.companyDetails?.tradingName || '',
      'ACN': data.companyDetails?.acn || '',
      'ABN': data.companyDetails?.abn || '',
      'ADDRESS': data.companyDetails?.address || '',
      'WEBSITE': data.companyDetails?.website || '',
      'PHONE': data.companyDetails?.phone || '',
      'EMAIL': data.companyDetails?.email || '',
      
      // ============ COMPANY OVERVIEW ============
      'SERVICES_SUMMARY': data.companyOverview?.servicesSummary || '',
      'COMPANY_STRUCTURE': data.companyOverview?.companyStructure || '',
      
      // ============ EXPERIENCE ============
      'YEARS_IN_BUSINESS': data.experience?.yearsInBusiness || '',
      'NUMBER_OF_STAFF': data.experience?.numberOfStaff || '',
      
      // Training Programs
      'TRAINING_AEI': data.experience?.aeiTraining ? '☑' : '☐',
      'TRAINING_ISANA': data.experience?.isanaTraining ? '☑' : '☐',
      'TRAINING_MARA': data.experience?.maraTraining ? '☑' : '☐',
      
      // Services Provided
      'PROVIDES_VISA': data.experience?.providesVisaAssistance ? '☑' : '☐',
      'VISA_YES': data.experience?.providesVisaAssistance ? '☑' : '☐',
      'VISA_NO': !data.experience?.providesVisaAssistance ? '☑' : '☐',
      
      'PROVIDES_TRAVEL': data.experience?.providesTravelAssistance ? '☑' : '☐',
      'TRAVEL_YES': data.experience?.providesTravelAssistance ? '☑' : '☐',
      'TRAVEL_NO': !data.experience?.providesTravelAssistance ? '☑' : '☐',
      
      // Provider Numbers
      'VET_PROVIDERS': data.experience?.vetProviders || '',
      'HIGHER_PROVIDERS': data.experience?.higherProviders || '',
      
      // Formal Agreements
      'HAS_AGREEMENTS': data.experience?.hasFormalAgreements ? '☑' : '☐',
      'AGREEMENTS_YES': data.experience?.hasFormalAgreements ? '☑' : '☐',
      'AGREEMENTS_NO': !data.experience?.hasFormalAgreements ? '☑' : '☐',
      'AGREEMENTS_DETAILS': data.experience?.formalAgreementsDetails || '',
      
      // Sub-Agents
      'HAS_SUBAGENTS': data.experience?.hasSubAgents ? '☑' : '☐',
      'SUBAGENTS_YES': data.experience?.hasSubAgents ? '☑' : '☐',
      'SUBAGENTS_NO': !data.experience?.hasSubAgents ? '☑' : '☐',
      'SUBAGENTS_DETAILS': data.experience?.subAgentsDetails || '',
      
      // Student Numbers (Past 3 Years)
      'H_S_STU': data.experience?.highSchoolStudents || '',
      'RTO_STU': data.experience?.rtoStudents || '',
      'UNI_STU': data.experience?.universityStudents || '',
      'ELI_STU': data.experience?.elicosStudents || '',
      
      // Fees and Marketing
      'STUDENT_FEES': data.experience?.studentFees || '',
      'MARKETING_CHANNELS': data.experience?.marketingChannels || '',
      'POPULAR_COURSES': data.experience?.popularCourses || '',
      'TARGET_COUNTRIES': data.experience?.targetCountries || '',
      
      // ============ BENEFITS ============
      'BENEFITS_DESCRIPTION': data.benefits?.benefitsDescription || '',
      
      // ============ REFEREES ============
      // Referee 1
      'REFEREE_1_COMPANY': data.referees?.[0]?.companyName || '',
      'REFEREE_1_NAME': data.referees?.[0]?.contactName || '',
      'REFEREE_1_POSITION': data.referees?.[0]?.contactPosition || '',
      'REFEREE_1_PHONE': data.referees?.[0]?.phone || '',
      'REFEREE_1_EMAIL': data.referees?.[0]?.email || '',
      
      // Referee 2
      'REFEREE_2_COMPANY': data.referees?.[1]?.companyName || '',
      'REFEREE_2_NAME': data.referees?.[1]?.contactName || '',
      'REFEREE_2_POSITION': data.referees?.[1]?.contactPosition || '',
      'REFEREE_2_PHONE': data.referees?.[1]?.phone || '',
      'REFEREE_2_EMAIL': data.referees?.[1]?.email || '',
      
      // ============ ATTACHMENTS / DOCUMENTS ============
      'HAS_BUSINESS_REG': data.attachments?.hasBusinessRegistration ? '☑' : '☐',
      'HAS_CERTIFICATE': data.attachments?.hasCertificate ? '☑' : '☐',
      'CHECKLIST_BUSINESS_REG': data.attachments?.hasBusinessRegistration ? '☑' : '☐',
      'CHECKLIST_CERTIFICATE': data.attachments?.hasCertificate ? '☑' : '☐',
      
      // Training Certificate Details
      'TRAINING_CERT_NAME': data.experience?.trainingCertificate?.name || '',
      'TRAINING_CERT_URL': data.experience?.trainingCertificate?.url || '',
      
      // Additional Documents
      'ADDITIONAL_DOC_1_NAME': data.declaration?.additionalDocuments?.[0]?.name || '',
      'ADDITIONAL_DOC_1_URL': data.declaration?.additionalDocuments?.[0]?.url || '',
      'ADDITIONAL_DOC_2_NAME': data.declaration?.additionalDocuments?.[1]?.name || '',
      'ADDITIONAL_DOC_2_URL': data.declaration?.additionalDocuments?.[1]?.url || '',
      
      // ============ DECLARATION ============
      'DECL_INFO_TRUE': data.declaration?.informationTrue ? '☑' : '☐',
      'DECL_REFEREES_PROVIDED': data.declaration?.refereesProvided ? '☑' : '☐',
      'DECL_PROOF_ATTACHED': data.declaration?.proofAttached ? '☑' : '☐',
      'DECL_AGREE_TERMS': data.declaration?.agreeToTerms ? '☑' : '☐',
      'DECL_AGREE_PRIVACY': data.declaration?.agreeToPrivacy ? '☑' : '☐',
      'DECL_UNDERSTAND_RESP': data.declaration?.understandResponsibilities ? '☑' : '☐',
      
      'REPRESENTATIVE_NAME': data.declaration?.representativeName || '',
      'REPRESENTATIVE_POSITION': data.declaration?.representativePosition || '',
      'REPRESENTATIVE_SIGNATURE': data.declaration?.representativeSignature || 'Electronically signed',
      'DECLARATION_DATE': data.declaration?.date || new Date().toLocaleDateString(),
      
      // ============ SYSTEM DATA ============
      'AGENT_CODE': data.agentCode || '-',
      'COMMISSION_RATE': data.commissionRate || '0',
      'STATUS': data.status || 'new',
      'CREATED_DATE': data.createdAt?.seconds 
        ? new Date(data.createdAt.seconds * 1000).toLocaleDateString() 
        : new Date().toLocaleDateString(),
      'UPDATED_DATE': data.updatedAt?.seconds 
        ? new Date(data.updatedAt.seconds * 1000).toLocaleDateString() 
        : new Date().toLocaleDateString(),
    };
  };

  // Generate and Download Agent Agreement (no restrictions)
  const handleAgentAgreementForm = async () => {
    if (!initialData?.id) return;

    try {
      setError(''); // Clear previous errors
      
      // 1. Fetch template
      const response = await fetch(agentAgreementTemplatePath);
      if (!response.ok) throw new Error("Agent agreement template not found");

      const arrayBuffer = await response.arrayBuffer();
      const templateMapping = createAgentAgreementMapping(initialData);

      // 2. Fill DOCX template
      const filledDoc = processDocxTemplate(arrayBuffer, templateMapping);

      // 3. Download the file
      const url = window.URL.createObjectURL(filledDoc);
      const link = document.createElement('a');
      link.href = url;
      link.download = `agent-agreement-${initialData.companyDetails?.companyName || initialData.id}.docx`;
      document.body.appendChild(link);
      link.click();
      
      // 4. Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError(err.message);
      console.error("Error generating agent agreement:", err);
    }
  };

  return (
    <div>
      <SectionTitle icon={CheckCircle}>Actions</SectionTitle>
      <div className="workflow-panel" style={{
        border: "1px solid #ddd",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "20px",
        background: "#f9fbfd"
      }}>
        <ol style={{ paddingLeft: "20px", margin: 0 }}>
          <li style={{ marginBottom: "20px" }}>
            <strong>Generate Agent Agreement:</strong> Download the agent agreement document at any time.
          </li>
          <li style={{ marginBottom: "20px" }}>
            <strong>Accept Agent Application:</strong> If agent is approved and onboarded, accept the agent application.
          </li>
          <li style={{ marginBottom: "20px" }}>
            <strong>Update Agent Record:</strong> Update the agent code as well as commissions for the agent.
          </li>
        </ol>
        <p style={{ marginTop: "12px", fontSize: "0.9em", color: "#555" }}>
          ⚡ The agreement can be generated at any time. Acceptance cannot be undone.
        </p>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24} style={{ marginBottom: 8 }}>
          <Button
            icon={ArrowRightCircle}
            onClick={handleAgentAgreementForm}
            // NO RESTRICTIONS - can generate anytime
            style={{
              width: '100%',
              padding: '16px',
              fontSize: 16,
              background: '#4dd0e1',
              color: '#006064',
              cursor: 'pointer',
            }}
          >
            Generate & Download Agent Agreement
          </Button>
        </Col>

        <Col span={24} style={{ marginBottom: 8 }}>
          <Button
            icon={CheckCircle}
            onClick={handleAcceptAgent}
            disabled={!pending}
            style={{
              width: '100%',
              padding: '16px',
              fontSize: 16,
              background: pending ? '#4dd0e1' : '#b2ebf2',
              color: pending ? '#006064' : '#757575',
              cursor: pending ? 'pointer' : 'not-allowed',
            }}
          >
            {!pending ? 'Agent Accepted' : 'Accept Agent'}
          </Button>
          <p>
            {initialData?.agentAcceptedAt ? (
              <em style={{ fontSize: 12 }}>
                Last updated at: {new Date(initialData?.agentAcceptedAt).toLocaleString()}
              </em>
            ) : null}
          </p>
        </Col>
      </Row>

      {error && (
        <p style={{ color: '#c62828', fontSize: 12, marginTop: 8 }}>
          Error: {error}
        </p>
      )}
    </div>
  );
}